/** @file readKeys: clip keys from a command input (integer frames, each once, node keys, events). */
import type { ClipKey, NodeKey } from '@zilo/pose3d-format/types/clip';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { NODE_KEYS_CHECK } from './nodeKeysCheck';
import { readEvents } from './readEvents';

const EASE = checkEnum(['linear', 'easeIn', 'easeOut', 'easeInOut', 'step']);
const HOLD = checkNumber({ int: true, min: 1 });

function readKey(value: unknown, path: string): ClipKey {
  const r = readInput(value, path, ['frame', 'nodes', 'ease', 'hold', 'events']);
  return compact({
    frame: r.int('frame', { min: 0 }), nodes: r.check('nodes', NODE_KEYS_CHECK) as Record<string, NodeKey>,
    ease: r.opt('ease', (k) => r.check(k, EASE) as ClipKey['ease']), hold: r.opt('hold', (k) => r.check(k, HOLD) as number),
    events: r.opt('events', (k) => readEvents(r.raw(k), r.at(k))),
  });
}

/** `value` as clip keys at `path`; `VALIDATION` for a bad key or a frame given twice. */
export function readKeys(value: unknown, path: string): ClipKey[] {
  if (!Array.isArray(value)) return failField(path, 'array of keys', value);
  const keys = value.map((k, i) => readKey(k, `${path}/${String(i)}`));
  keys.forEach((k, i) => { if (keys.findIndex((o) => o.frame === k.frame) !== i) failField(`${path}/${String(i)}/frame`, 'a frame given once', k.frame); });
  return keys;
}
